import { Button, Label, TextField, Theme } from '@busybusy/webapp-react-ui';
import { EquipmentMake, EquipmentMakeCreateInput, EquipmentMakeUpdateInput } from '__generated__/graphql';
import classNames from 'classnames';
import HeaderDialog from 'components/foundation/dialogs/HeaderDialog/HeaderDialog';
import useEquipmentMakes from 'containers/manage-euipment/hooks/useEquipmentMakes';
import DeleteConfirmationDialog from 'containers/photos/DeleteConfirmationDialog/DeleteConfirmationDialog';
import { useToastOpen } from 'contexts/ToastContext';
import { useOpenable } from 'hooks';
import { t } from 'i18next';
import { isNull } from 'lodash';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { ClassName } from 'types/ClassName';
import { isValidString } from 'utils/stringUtils';
import EquipmentCatMakeModelDeleteProhibited from '../EquipmentCatMakeModelDeleteProhibitedDialog/EquipmentCatMakeModelDeleteProhibitedDialog';
import './CreateEquipmentMakeDialog.scss';

export interface ICreateEquipmentMakeDialogProps {
  className?: ClassName;
  isOpen: boolean;
  selectedRow: EquipmentMake | null;
  canDelete: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onDelete: () => void;
}

const CreateEquipmentMakeDialog = (props: ICreateEquipmentMakeDialogProps) => {
  const { className, isOpen, selectedRow, canDelete, onClose, onSubmit, onDelete } = props;

  const deleteDialog = useOpenable();
  const deleteProhibitedDialog = useOpenable();
  const toast = useToastOpen();
  const { createEquipmentMake, editEquipmentMake } = useEquipmentMakes();

  const [name, setName] = useState('');

  useEffect(() => {
    if (!isNull(selectedRow)) {
      setName(selectedRow.title ?? '');
    } else {
      resetAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow]);

  const handleSubmit = async () => {
    if (!isValidString(name)) {
      toast({ label: t('Name is required'), theme: Theme.DANGER });
      return;
    }

    if (!isNull(selectedRow)) {
      const editInput: EquipmentMakeUpdateInput = {
        id: selectedRow.id,
        title: name,
      };

      try {
        await editEquipmentMake(editInput);
        resetAll();
        onSubmit();
      } catch (error) {
        toast({ label: t('Error editing equipment make'), theme: Theme.DANGER });
        console.error('Error:', error);
      }
    } else {
      const createInput: EquipmentMakeCreateInput = {
        title: name,
        createdOn: DateTime.now().toISO(),
      };

      try {
        await createEquipmentMake(createInput);
        resetAll();
        onSubmit();
      } catch (error) {
        toast({ label: t('Error creating equipment make'), theme: Theme.DANGER });
        console.error('Error:', error);
      }
    }
  };

  const handleClose = () => {
    onClose();
    resetAll();
  };

  const resetAll = () => {
    setName('');
  };

  const handleDelete = () => {
    resetAll();
    deleteDialog.close();
    onDelete();
  };

  const handleDeleteClick = () => {
    if (canDelete) {
      deleteDialog.open();
    } else {
      deleteProhibitedDialog.open();
    }
  };

  const classes = classNames('create-equipment-make-dialog', className);

  return (
    <HeaderDialog
      className={classes}
      isOpen={isOpen}
      onClose={handleClose}
      title={t(`${!isNull(selectedRow) ? 'Edit' : 'Create'} Make`)}
      divider={false}
    >
      <div className="mx-8">
        <Label required={true}>{t('Name')}</Label>
        <TextField value={name} restrictTo={{ maxLength: 200 }} onChange={setName} />
        <Button className={'my-5'} type={'primary'} onClick={handleSubmit}>
          {!isNull(selectedRow) ? t('Update') : t('Create')}
        </Button>

        <div className={'delete-button mx-2'} onClick={handleDeleteClick}>
          {t('Delete')}
        </div>
      </div>
      <DeleteConfirmationDialog
        isOpen={deleteDialog.isOpen}
        title={t('Delete make?')}
        onClose={deleteDialog.close}
        onDelete={handleDelete}
        showHeaderDivider={false}
      >
        {t('If you delete this item it will be gone forever. Are you sure you want to proceed?')}
      </DeleteConfirmationDialog>
      <EquipmentCatMakeModelDeleteProhibited
        isOpen={deleteProhibitedDialog.isOpen}
        onClose={deleteProhibitedDialog.close}
        type={'make'}
      />
    </HeaderDialog>
  );
};

export default CreateEquipmentMakeDialog;
